// Rotas do site. Definidas em código (sem gerador) para ficar fácil de ler.
//
//   /entrar                 login, 2FA e cadastro do app autenticador
//   /                       início: continuar assistindo + cursos
//   /cursos/$slug           página do curso
//   /aula/$lessonId         player
//   /busca?q=               busca
//   /tv?codigo=             autorizar a TV a entrar (código mostrado na TV)
//   /conta                  minha conta
//   /trocar-senha           obrigatória quando a senha é provisória
//   /console/...            console do admin master (owner e admin), incl. /console/envios

import type { Api } from "@acervo/api-client";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  type ParsedLocation,
} from "@tanstack/react-router";
import { AppShell } from "./components/shell";
import { ErrorBox, Spinner } from "./components/ui";
import { isAdmin } from "./lib/api";
import { AccountPage, ChangePasswordPage } from "./pages/account";
import { AdminLayout } from "./pages/admin/layout";
import { AuditPage } from "./pages/admin/audit";
import { AdminCoursesPage } from "./pages/admin/courses";
import { OverviewPage } from "./pages/admin/overview";
import { UserDetailPage } from "./pages/admin/user-detail";
import { UploadsPage } from "./pages/admin/uploads";
import { UsersPage } from "./pages/admin/users";
import { CoursePage } from "./pages/course";
import { HomePage } from "./pages/home";
import { LoginPage } from "./pages/login";
import { NotFoundPage } from "./pages/not-found";
import { SearchPage } from "./pages/search";
import { TvPage } from "./pages/tv";

// O player (Shaka) é carregado só quando alguém abre uma aula.
const LessonPage = lazyRouteComponent(() => import("./pages/lesson"));

export interface RouterContext {
  api: Api;
  queryClient: QueryClient;
}

/** Só aceita voltar para um caminho deste site (evita redirecionamento aberto). */
export function safeReturn(v: unknown): string | undefined {
  return typeof v === "string" && v.startsWith("/") && !v.startsWith("//") && !v.startsWith("/\\") ? v : undefined;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-xl p-8">
      <ErrorBox error={error} onRetry={reset} />
    </div>
  ),
  pendingComponent: () => <Spinner />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/entrar",
  validateSearch: (s: Record<string, unknown>): { voltar?: string } => ({ voltar: safeReturn(s.voltar) }),
  beforeLoad: ({ context, search, cause }) => {
    // "stay": a sessão acabou de começar nesta página; quem decide o próximo
    // passo é o próprio login (ex.: mostrar os códigos de recuperação).
    if (context.api.session && cause !== "stay") throw redirect({ to: search.voltar ?? "/" });
  },
  component: LoginPage,
});

function requireSession(context: RouterContext, location: ParsedLocation) {
  const s = context.api.session;
  if (!s) throw redirect({ to: "/entrar", search: { voltar: location.href === "/" ? undefined : location.href } });
  if (s.user.must_change_password && location.pathname !== "/trocar-senha") throw redirect({ to: "/trocar-senha" });
  return s;
}

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  beforeLoad: ({ context, location }) => {
    requireSession(context, location);
  },
  component: AppShell,
});

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: "/", component: HomePage });
const courseRoute = createRoute({ getParentRoute: () => appRoute, path: "/cursos/$slug", component: CoursePage });
const lessonRoute = createRoute({ getParentRoute: () => appRoute, path: "/aula/$lessonId", component: LessonPage });
const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/busca",
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({
    q: typeof s.q === "string" && s.q ? s.q : undefined,
  }),
  component: SearchPage,
});
const tvRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tv",
  validateSearch: (s: Record<string, unknown>): { codigo?: string } => ({
    codigo: typeof s.codigo === "string" && s.codigo ? s.codigo : undefined,
  }),
  component: TvPage,
});
const accountRoute = createRoute({ getParentRoute: () => appRoute, path: "/conta", component: AccountPage });
const changePasswordRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/trocar-senha",
  component: ChangePasswordPage,
});

const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/console",
  beforeLoad: ({ context }) => {
    if (!isAdmin(context.api.session)) throw redirect({ to: "/" });
  },
  component: AdminLayout,
});
const overviewRoute = createRoute({ getParentRoute: () => adminRoute, path: "/", component: OverviewPage });
const usersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/usuarios",
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({
    q: typeof s.q === "string" && s.q ? s.q : undefined,
  }),
  component: UsersPage,
});
const userDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/usuarios/$userId",
  component: UserDetailPage,
});
const adminCoursesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/cursos",
  component: AdminCoursesPage,
});
const uploadsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/envios",
  validateSearch: (s: Record<string, unknown>): { curso?: string } => ({
    curso: typeof s.curso === "string" && s.curso ? s.curso : undefined,
  }),
  component: UploadsPage,
});
const auditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/auditoria",
  validateSearch: (s: Record<string, unknown>): { action?: string; target?: string } => ({
    action: typeof s.action === "string" && s.action ? s.action : undefined,
    target: typeof s.target === "string" && s.target ? s.target : undefined,
  }),
  component: AuditPage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    homeRoute,
    courseRoute,
    lessonRoute,
    searchRoute,
    tvRoute,
    accountRoute,
    changePasswordRoute,
    adminRoute.addChildren([overviewRoute, usersRoute, userDetailRoute, adminCoursesRoute, uploadsRoute, auditRoute]),
  ]),
]);

export function makeRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0, // o cache fica com o TanStack Query
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
